import { useMemo } from 'react'

import { useUnit } from 'effector-react'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { useToast } from '@/components/ui/toast'
import { $creatorId } from '@/models/creators.ts'
import { handleError } from '@/utils/error'

import {
  getLinkTrackingById,
  updateLinkTrackingPromotionCost,
  updateLinkTrackingVisibility,
} from '../data/service'
import type {
  UpdateLinkTrackingPromotionCostForm,
  UpdateLinkTrackingVisibilityForm,
} from '../types'
import { type LinkType } from '../types'

interface UseLinkTrackinById {
  type: LinkType
  id: number
}

export function useLinkTrackingById({ type, id }: UseLinkTrackinById) {
  const creatorId = useUnit($creatorId)
  const { data, isFetching, isIdle } = useQuery(
    ['linkTrackingById', creatorId, type, id],
    () => {
      return getLinkTrackingById({
        creatorId: creatorId!,
        type,
        id,
      })
    },
    {
      retry: false,
      enabled: !!creatorId,
      onError: (error) =>
        handleError(error, 'Failed to get link tracking data'),
    },
  )
  const updatePromotionCost = useUpdateLinkTrackingPromotionCost()
  const toast = useToast()

  function updatePromotionCostHandler(newPromotionCost: number | undefined) {
    updatePromotionCost.mutateAsync({
      linkId: id,
      type,
      promotionCost: newPromotionCost,
    })
    toast.success('Promotion cost updated')
  }

  return useMemo(
    () => {
      return {
        data: data,
        updatePromotionCost: updatePromotionCostHandler,
        isFetching: isFetching || isIdle,
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, isFetching, isIdle],
  )
}

export function useUpdateLinkTrackingPromotionCost() {
  const client = useQueryClient()
  const creatorId = useUnit($creatorId)

  return useMutation(
    (promotionCostData: UpdateLinkTrackingPromotionCostForm) => {
      if (!creatorId) {
        throw new Error('No creator selected')
      }

      return updateLinkTrackingPromotionCost(creatorId, promotionCostData)
    },
    {
      onMutate: (update) => {
        client.setQueryData(['linkTrackingById', creatorId], update)
      },
      onSettled: () =>
        client.invalidateQueries(['linkTrackingById', creatorId]),
      onError: (error) => {
        handleError(error, 'Failed to update link tracking promotion cost')
      },
    },
  )
}

export function useUpdateLinkTrackingVisibility() {
  const creatorId = useUnit($creatorId)

  return useMutation((visibilityData: UpdateLinkTrackingVisibilityForm) => {
    if (!creatorId) {
      throw new Error('No creator selected')
    }

    return updateLinkTrackingVisibility(creatorId, visibilityData)
  })
}
